import type { MaybeRefOrGetter } from 'vue';

import { useIntervalFn } from '@vueuse/core';
import { computed, ref, toValue, watch } from 'vue';

function computeRemainingSeconds(utcExpiresAt: string | null | undefined): number {
  if (!utcExpiresAt)
    return 0;
  const expiresMs = new Date(utcExpiresAt).getTime();
  if (Number.isNaN(expiresMs))
    return 0;
  return Math.max(0, Math.floor((expiresMs - Date.now()) / 1000));
}

export function useCountdown(utcExpiresAt: MaybeRefOrGetter<string | null | undefined>) {
  const remainingSeconds = ref(computeRemainingSeconds(toValue(utcExpiresAt)));

  const { pause, resume } = useIntervalFn(() => {
    remainingSeconds.value = computeRemainingSeconds(toValue(utcExpiresAt));
  }, 1000, { immediate: false });

  watch(
    () => toValue(utcExpiresAt),
    (value) => {
      remainingSeconds.value = computeRemainingSeconds(value);
      if (value) {
        resume();
      }
      else {
        pause();
      }
    },
    { immediate: true },
  );

  const formattedTime = computed(() => {
    const minutes = Math.floor(remainingSeconds.value / 60);
    const seconds = remainingSeconds.value % 60;
    return `${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
  });

  const isExpired = computed(() => remainingSeconds.value <= 0);

  return {
    remainingSeconds,
    formattedTime,
    isExpired,
  };
}
